'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

export interface BrowseTabsProps {
  /** Section heading, rendered above the pills. */
  children: ReactNode;
  topics: string[];
  /** Server-rendered panel per topic; every panel stays in the SSR HTML. */
  panels: ReactNode[];
}

// seo-pages HubsBrowse.astro `.hubs-pill-label` global styles; active pill is green fill.
const PILL =
  'cursor-pointer whitespace-nowrap rounded-full border border-[var(--content-neutral-lighter)] bg-background px-4 py-2 font-sans text-sm font-medium text-foreground transition-all duration-200 hover:border-primary hover:text-primary focus-visible:focus-ring aria-selected:border-primary aria-selected:bg-primary aria-selected:text-primary-foreground';

/**
 * "Browse Articles" topic tabs (seo-pages CategoriesSection.astro). seo-pages uses CSS-only radio
 * tabs; this keeps the same markup cost but adds the ARIA tabs pattern (arrow/Home/End keys).
 */
export function BrowseTabs({ children, topics, panels }: BrowseTabsProps) {
  const [active, setActive] = useState(0);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (index: number) => {
    const next = (index + topics.length) % topics.length;
    setActive(next);
    tabs.current[next]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const target = { ArrowRight: active + 1, ArrowLeft: active - 1, Home: 0, End: -1 }[event.key];
    if (target === undefined) return;
    event.preventDefault();
    select(target);
  };

  return (
    <>
      <div className="mb-8 flex flex-col gap-6">
        {children}
        <div className="flex flex-col gap-2">
          <span className="mr-3 text-nowrap text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Explore by:
          </span>
          <div className="flex overflow-x-auto [scrollbar-width:none]">
            <div
              aria-label="Browse categories"
              className="-mx-4 flex gap-2 px-4 py-1 md:mx-0 md:px-0"
              onKeyDown={onKeyDown}
              role="tablist"
            >
              {topics.map((topic, index) => (
                <button
                  aria-controls={`learn-hub-panel-${index}`}
                  aria-selected={index === active}
                  className={PILL}
                  id={`learn-hub-tab-${index}`}
                  key={topic}
                  onClick={() => setActive(index)}
                  ref={node => {
                    tabs.current[index] = node;
                  }}
                  role="tab"
                  tabIndex={index === active ? 0 : -1}
                  type="button"
                >
                  {topic}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {panels.map((panel, index) => (
        <div
          aria-labelledby={`learn-hub-tab-${index}`}
          hidden={index !== active}
          id={`learn-hub-panel-${index}`}
          key={topics[index]}
          role="tabpanel"
        >
          {panel}
        </div>
      ))}
    </>
  );
}

export interface ScrollCarouselProps {
  label: string;
  /** `<li>` slides. */
  children: ReactNode;
}

// seo-pages Carousel.astro alternativeLayout arrow buttons.
const ARROW =
  'group/arrow flex size-9 cursor-pointer items-center justify-center rounded-full bg-background shadow transition-colors duration-300 focus-visible:focus-ring [@media(hover:hover)_and_(pointer:fine)]:hover:bg-primary';
const ARROW_ICON =
  'size-4 text-primary transition-colors duration-300 [@media(hover:hover)_and_(pointer:fine)]:group-hover/arrow:text-primary-foreground';

/**
 * Native scroll-snap carousel standing in for seo-pages' 427-line init-carousels.ts: the track
 * scrolls by touch/trackpad, and the buttons page it by one viewport.
 */
export function ScrollCarousel({ label, children }: ScrollCarouselProps) {
  const track = useRef<HTMLUListElement>(null);
  const page = (direction: 1 | -1) =>
    track.current?.scrollBy({ left: direction * track.current.clientWidth, behavior: 'smooth' });

  return (
    <div
      aria-label={label}
      aria-roledescription="carousel"
      className="flex w-full flex-col items-center gap-6"
      role="region"
    >
      <ul
        className="flex w-full snap-x snap-mandatory gap-2 overflow-x-auto pb-14 pt-2 [scrollbar-width:none]"
        ref={track}
      >
        {children}
      </ul>
      <div className="flex items-center gap-6">
        <button
          aria-label={`Scroll ${label} left`}
          className={ARROW}
          onClick={() => page(-1)}
          type="button"
        >
          <ChevronLeft aria-hidden="true" className={ARROW_ICON} />
        </button>
        <button
          aria-label={`Scroll ${label} right`}
          className={ARROW}
          onClick={() => page(1)}
          type="button"
        >
          <ChevronRight aria-hidden="true" className={ARROW_ICON} />
        </button>
      </div>
    </div>
  );
}
